import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU, formatRelativeDays } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { QASkeleton } from './QASkeleton';
import type { TimelineEntry, TimelineKind, TimelineTab as TabTarget } from '../../../shared/api';
import {
  FileText, ScrollText, ShieldCheck, ClipboardList, Gauge, Mic, type LucideIcon,
} from 'lucide-react';

interface KindMeta {
  label: string;
  icon: LucideIcon;
  /** Tailwind text-colour class (also drives the icon + rail dot). */
  tone: string;
}

const KIND_META: Record<TimelineKind, KindMeta> = {
  file_note: { label: 'File note', icon: FileText, tone: 'text-ink-soft' },
  advice: { label: 'Advice', icon: ScrollText, tone: 'text-rust' },
  compliance: { label: 'Compliance', icon: ShieldCheck, tone: 'text-forest' },
  intake_fact_find: { label: 'Fact-find', icon: ClipboardList, tone: 'text-brass-deep' },
  intake_risk_profile: { label: 'Risk profile', icon: Gauge, tone: 'text-brass-deep' },
  intake_file_note: { label: 'File-Note.AI', icon: Mic, tone: 'text-brass-deep' },
};

const FILTER_OPTIONS: Array<{ value: '' | TimelineKind; label: string }> = [
  { value: '', label: 'All activity' },
  { value: 'file_note', label: 'File notes' },
  { value: 'advice', label: 'Advice' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'intake_fact_find', label: 'Fact-finds' },
  { value: 'intake_risk_profile', label: 'Risk profiles' },
  { value: 'intake_file_note', label: 'File-Note.AI sessions' },
];

const LONG_DETAIL = 240;

export function TimelineTab({
  clientId,
  onOpenTab,
}: {
  clientId: string;
  onOpenTab?: (tab: TabTarget) => void;
}) {
  const [kind, setKind] = useState<'' | TimelineKind>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', clientId, 'timeline', kind],
    queryFn: () =>
      api<{ data: TimelineEntry[] }>(
        `/api/clients/${clientId}/timeline${kind ? `?kind=${kind}` : ''}`,
      ),
  });

  const entries = data?.data ?? [];

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between gap-3 mb-5 flex-col sm:flex-row">
        <div>
          <p className="qa-eyebrow mb-1">Activity timeline</p>
          <p className="text-sm text-ink-mute">
            {isLoading ? 'Loading activity…' : `${entries.length} event${entries.length === 1 ? '' : 's'}${kind ? ' in this view' : ''}`}
          </p>
        </div>
        <select
          className="qa-select sm:w-56"
          aria-label="Filter timeline by activity type"
          value={kind}
          onChange={(e) => setKind(e.target.value as '' | TimelineKind)}
        >
          {FILTER_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <QASkeleton variant="cards" rows={5} />
      ) : isError ? (
        <QAEmpty inline eyebrow="Couldn't load" title="The timeline failed to load." >
          Try switching tabs and back, or refresh the page.
        </QAEmpty>
      ) : entries.length === 0 ? (
        <QAEmpty
          inline
          eyebrow="Nothing yet"
          title={kind ? 'No activity of this type.' : 'No recorded activity for this client.'}
        >
          {kind ? 'Clear the filter to see everything.' : 'File notes, advice, compliance updates and suite intake will appear here as they happen.'}
        </QAEmpty>
      ) : (
        <ol className="relative border-l border-hairline ml-3" data-testid="client-timeline">
          {entries.map((e) => {
            const meta = KIND_META[e.kind];
            const Icon = meta.icon;
            const isLong = !!e.detail && e.detail.length > LONG_DETAIL;
            const isOpen = expanded.has(e.id);
            const detail = e.detail && isLong && !isOpen ? `${e.detail.slice(0, LONG_DETAIL)}…` : e.detail;
            return (
              <li key={e.id} className="relative pl-8 pb-6 last:pb-0">
                <span
                  className={`absolute -left-[9px] top-1 grid place-items-center w-[18px] h-[18px] rounded-full bg-paper border border-hairline ${meta.tone}`}
                  aria-hidden="true"
                >
                  <Icon size={11} strokeWidth={2.2} />
                </span>
                <div className="qa-card p-4">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <h4 className="text-sm font-semibold text-ink">{e.title}</h4>
                    <span className="qa-mono text-[11px] text-ink-mute whitespace-nowrap">
                      {formatDateAU(e.at)} · {formatRelativeDays(e.at)}
                    </span>
                  </div>

                  {(e.badges && e.badges.length > 0) && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className={`qa-badge qa-badge--mute ${meta.tone}`}>{meta.label}</span>
                      {e.badges.map((b, i) => (
                        <span key={i} className="qa-badge qa-badge--mute">{b}</span>
                      ))}
                    </div>
                  )}
                  {(!e.badges || e.badges.length === 0) && (
                    <div className="mt-2">
                      <span className={`qa-badge qa-badge--mute ${meta.tone}`}>{meta.label}</span>
                    </div>
                  )}

                  {detail && (
                    <p className="text-sm text-ink-soft whitespace-pre-wrap mt-3">{detail}</p>
                  )}

                  <div className="flex items-center gap-4 mt-3">
                    {isLong && (
                      <button
                        type="button"
                        className="text-[11px] uppercase tracking-widest font-bold text-ink-mute hover:text-ink"
                        onClick={() => toggle(e.id)}
                      >
                        {isOpen ? 'Show less' : 'Show more'}
                      </button>
                    )}
                    {onOpenTab && (
                      <button
                        type="button"
                        className="text-[11px] uppercase tracking-widest font-bold text-rust hover:text-rust-deep"
                        onClick={() => onOpenTab(e.tab)}
                      >
                        Open {meta.label.toLowerCase()} →
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
